import React, { useState, useEffect } from 'react';
import { ingredientTraceCodeApiService } from '../../../services/ingredientTraceCodeApiService';
import { LotNumberOption } from '../../../types/componentRecord';

export interface DoughIngredient {
  id: string;
  ingredientName: string;
  lotNumbers: string[];
  quantity?: number;
  unit?: string;
}

interface DoughIngredientsTableProps {
  ingredients: DoughIngredient[];
  isEditing: boolean;
  onIngredientChange?: (id: string, field: keyof DoughIngredient, value: string | string[] | number | undefined) => void;
  timesProduced?: number;
}

export const DoughIngredientsTable: React.FC<DoughIngredientsTableProps> = ({
  ingredients,
  isEditing,
  onIngredientChange,
  timesProduced = 1
}) => {
  // State to store available lot numbers (trace codes) per ingredient
  const [lotNumbersByIngredient, setLotNumbersByIngredient] = useState<Record<string, LotNumberOption[]>>({});
  const [loadingIngredients, setLoadingIngredients] = useState<Record<string, boolean>>({});

  // Fetch trace codes for all ingredients when editing mode is enabled
  useEffect(() => {
    if (isEditing && ingredients.length > 0) {
      ingredients.forEach(ingredient => {
        if (!lotNumbersByIngredient[ingredient.ingredientName] && !loadingIngredients[ingredient.ingredientName]) {
          fetchTraceCodesForIngredient(ingredient.ingredientName);
        }
      });
    }
  }, [isEditing, ingredients]);

  const fetchTraceCodesForIngredient = async (ingredientName: string) => {
    setLoadingIngredients(prev => ({ ...prev, [ingredientName]: true }));
    try {
      const traceCodes = await ingredientTraceCodeApiService.fetchTraceCodesByIngredientName(ingredientName);
      setLotNumbersByIngredient(prev => ({ ...prev, [ingredientName]: traceCodes }));
    } catch (error) {
      console.error(`Failed to fetch trace codes for ${ingredientName}:`, error);
      setLotNumbersByIngredient(prev => ({ ...prev, [ingredientName]: [] }));
    } finally {
      setLoadingIngredients(prev => ({ ...prev, [ingredientName]: false }));
    }
  };

  const handleLotNumberToggle = (ingredientId: string, lotNumber: string, currentLotNumbers: string[]) => {
    if (onIngredientChange) {
      const isSelected = currentLotNumbers.includes(lotNumber);
      const newLotNumbers = isSelected
        ? currentLotNumbers.filter(ln => ln !== lotNumber)
        : [...currentLotNumbers, lotNumber];
      onIngredientChange(ingredientId, 'lotNumbers', newLotNumbers);
    }
  };

  return (
    <div>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e0e0e0'
              }}
            >
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Ingredient Name
              </th>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Lot Numbers
              </th>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  width: '120px'
                }}
              >
                Quantity
              </th>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  width: '100px'
                }}
              >
                Unit
              </th>
            </tr>
          </thead>
          <tbody>
            {ingredients.map(ingredient => {
              const availableLots = lotNumbersByIngredient[ingredient.ingredientName] || [];
              const isLoading = loadingIngredients[ingredient.ingredientName];

              return (
                <tr
                  key={ingredient.id}
                  style={{
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <td style={{ padding: '12px' }}>
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      {ingredient.ingredientName}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {isEditing ? (
                      isLoading ? (
                        <div style={{ padding: '8px', color: '#999', fontSize: '14px' }}>
                          Loading...
                        </div>
                      ) : availableLots.length > 0 ? (
                        <div
                          style={{
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '150px',
                            overflowY: 'auto',
                            padding: '8px',
                            backgroundColor: 'white'
                          }}
                        >
                          {availableLots.map(lot => (
                            <label
                              key={lot.lotNumber}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={ingredient.lotNumbers.includes(lot.lotNumber)}
                                onChange={() => handleLotNumberToggle(ingredient.id, lot.lotNumber, ingredient.lotNumbers)}
                                style={{ marginRight: '8px' }}
                              />
                              <span style={{ fontFamily: 'monospace' }}>{lot.lotNumber}</span>
                              {lot.receivingDate && (
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '12px',
                                    color: '#666'
                                  }}
                                >
                                  {new Date(lot.receivingDate).toLocaleDateString()}
                                </span>
                              )}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: '8px',
                            color: '#999',
                            fontSize: '14px',
                            fontStyle: 'italic',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: '#f9f9f9'
                          }}
                        >
                          No lots available
                        </div>
                      )
                    ) : (
                      <span
                        style={{
                          fontSize: '13px',
                          color: '#333',
                          fontFamily: 'monospace',
                          display: 'block'
                        }}
                      >
                        {ingredient.lotNumbers.join(', ') || '-'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      {ingredient.quantity !== undefined ? (
                        timesProduced > 1 ? (
                          <>
                            {ingredient.quantity * timesProduced}
                            <span style={{ color: '#888', fontSize: '12px', marginLeft: '4px' }}>
                              ({ingredient.quantity} x {timesProduced})
                            </span>
                          </>
                        ) : (
                          ingredient.quantity
                        )
                      ) : '-'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      {ingredient.unit || '-'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {ingredients.length === 0 && (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#999',
            fontSize: '14px',
            fontStyle: 'italic'
          }}
        >
          No ingredients added yet.
        </div>
      )}
    </div>
  );
};
